import {
  AttachmentImage,
  AttachmentLink,
} from "@/components/ui/attachment-media";
import { FrameLink } from "@/components/ui/frame-link";
import { MarkdownContent } from "@/components/ui/markdown-content";
import type { TaskComment } from "@/api/tasks";
import { formatRelativeTime, useT } from "@/i18n";
import { panelsTaskCommentsMessages } from "@/i18n/messages/panels-task-comments";
import { Button, Icons } from "@/components/ui";
import { attachmentFormat, attachmentIsImage } from "@/lib/composer-attachment";

interface TaskCommentItemProps {
  comment: TaskComment;
  /** The task's project, where the comment's frame chip opens. */
  projectId: string;
  resolveAgentName: (agentId: string | null) => string;
  onImageClick?: (src: string) => void;
}

/** One comment: a 28px initials avatar, name and time, then the text. */
export function TaskCommentItem({
  comment,
  projectId,
  resolveAgentName,
  onImageClick,
}: TaskCommentItemProps) {
  const t = useT(panelsTaskCommentsMessages);
  const author =
    comment.agent_name ||
    (comment.agent_id
      ? resolveAgentName(comment.agent_id)
      : comment.user
        ? t("you")
        : t("system"));
  const mentionedAgent = comment.mentioned_agent_id
    ? resolveAgentName(comment.mentioned_agent_id)
    : null;

  return (
    <div
      className="flex gap-2.5 px-2 py-1.5"
      data-testid={`task-comment-${comment.id}`}
    >
      <span
        aria-hidden="true"
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[var(--color-surface-selected)] text-appkit-small font-semibold text-primary"
      >
        {author.charAt(0).toUpperCase()}
      </span>
      <div className="min-w-0 flex-1">
        <p className="flex min-w-0 items-baseline gap-1.5">
          <span className="truncate text-appkit-caption font-semibold text-primary">
            {author}
          </span>
          {mentionedAgent ? (
            <span className="shrink-0 text-appkit-small font-medium text-muted">
              {t("toAgent", { name: mentionedAgent })}
            </span>
          ) : null}
          <span className="shrink-0 text-appkit-small font-medium text-muted">
            {formatRelativeTime(comment.created_at, { style: "narrow" })}
          </span>
        </p>
        <MarkdownContent
          content={comment.content}
          className="mt-0.5 text-appkit-body text-primary"
        />
        {comment.frame && comment.frame_title ? (
          <div className="mt-1">
            <FrameLink
              frameId={comment.frame}
              projectId={projectId}
              title={comment.frame_title}
              frameType={comment.frame_type ?? undefined}
            />
          </div>
        ) : null}
        {comment.images && comment.images.length > 0 ? (
          <div className="mt-2 flex flex-wrap gap-2">
            {comment.images.map((image) => {
              const attachment = {
                ...image,
                filename: image.original_filename,
                contentType: image.content_type,
              };
              const imageFile = attachmentIsImage(attachment);
              const url = image.url;
              const contents = (
                <>
                  {imageFile ? (
                    <AttachmentImage
                      src={url}
                      alt={image.original_filename || t("commentAttachment")}
                      className="h-10 w-10 rounded-preview object-cover"
                    />
                  ) : (
                    <Icons.FileText
                      className="h-10 w-10 p-2 text-muted"
                      aria-hidden="true"
                    />
                  )}
                  <span className="min-w-0 max-w-40 text-left">
                    <span className="block truncate text-appkit-caption text-primary">
                      {image.original_filename || t("screenshot")}
                    </span>
                    <span className="block text-appkit-mini text-muted">
                      {attachmentFormat(attachment)}
                    </span>
                  </span>
                </>
              );
              return imageFile ? (
                <Button
                  key={image.id}
                  type="button"
                  variant="ghost"
                  onClick={() => onImageClick?.(url)}
                  className="h-auto gap-2 rounded-preview p-1"
                  data-testid={`comment-image-${image.id}`}
                  aria-label={`Preview ${image.original_filename || "image"}`}
                >
                  {contents}
                </Button>
              ) : (
                <AttachmentLink
                  key={image.id}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 rounded-preview p-1 transition-opacity hover:opacity-80"
                  data-testid={`comment-file-${image.id}`}
                  aria-label={`${image.original_filename || t("attachedFile")} ${attachmentFormat(attachment)}`}
                >
                  {contents}
                </AttachmentLink>
              );
            })}
          </div>
        ) : null}
      </div>
    </div>
  );
}
